<script setup lang="ts">
import { computed, shallowRef } from 'vue'
import BLabel from '@/components/common/bLabel.vue'
import SvgSpinnersPulseRings3 from '~icons/svg-spinners/pulse-rings-3'
import AiScreeningTooltip from '@/components/recruitment/AiScreeningTooltip.vue'
import { Menu as VMenu } from 'floating-vue'
import { useI18n } from 'vue-i18n'

export type AiScreeningData = {
  status?: 'pending' | 'queued' | 'processing' | 'completed' | 'failed' | 'skipped' | null
  overallScore?: number | null
  overallSummary?: string | null
  error?: string | null
  resumeQuality?: {
    score?: number | null
    summary?: string
    strengths?: string[]
    weaknesses?: string[]
  } | null
  coverLetterQuality?: {
    score?: number | null
    summary?: string
    strengths?: string[]
    weaknesses?: string[]
  } | null
  jobFit?: {
    score?: number | null
    summary?: string
    matchPoints?: string[]
    gaps?: string[]
  } | null
}

const props = withDefaults(defineProps<{
  aiScreening?: AiScreeningData | null
  compact?: boolean
}>(), {
  compact: false,
})

const { t } = useI18n()
const isTooltipShown = shallowRef(false)

const scoreBg = (score: number | null | undefined) => {
  if (score == null) return 'disabled'
  if (score >= 8) return 'success-op'
  if (score >= 5) return 'warning-op'
  return 'error-op'
}

const isLoading = computed(() => {
  return ['pending', 'queued', 'processing'].includes(props.aiScreening?.status || '')
})

const hasResult = computed(() => props.aiScreening?.status === 'completed')

const statusLabel = computed(() => {
  const status = props.aiScreening?.status
  if (!status) return null
  return t(`modules.recruitment.aiScreening.status.${status}`)
})

const compactBg = computed(() => {
  if (isLoading.value) return 'disabled'
  if (hasResult.value) return scoreBg(props.aiScreening?.overallScore)
  return 'disabled'
})
</script>

<template>
  <VMenu
    v-if="compact && aiScreening?.status"
    v-model:shown="isTooltipShown"
    placement="right-start"
    :delay="{ show: 400, hide: 150 }"
    :triggers="['hover', 'focus']"
    :popper-triggers="['hover']"
    theme="tooltip"
  >
    <BLabel
      size="dense"
      :bg-color="compactBg"
      class="shrink-0 tabular-nums min-w-9 justify-center cursor-default"
      data-cy="ai-screening-badge"
      @click.stop
    >
      <template v-if="isLoading">…</template>
      <template v-else-if="hasResult">
        {{ aiScreening?.overallScore }}/10
      </template>
      <template v-else>
        —
      </template>
    </BLabel>
    <template #popper>
      <AiScreeningTooltip v-if="isTooltipShown && aiScreening" :ai-screening="aiScreening" />
    </template>
  </VMenu>

  <div
    v-else-if="aiScreening?.status"
    class="card-base p-4 flex flex-col gap-3"
    data-cy="ai-screening-panel"
  >
    <p class="subtitle">
      {{ t('modules.recruitment.aiScreening.title') }}
    </p>

    <template v-if="isLoading">
      <div class="flex items-center gap-2 text-sm text-subtitle" data-cy="ai-screening-pending">
        <SvgSpinnersPulseRings3 class="text-primary-600 text-lg shrink-0" />
        {{ t('modules.recruitment.aiScreening.pendingHint') }}
      </div>
    </template>

    <template v-else-if="hasResult">
      <div class="flex items-center gap-2 flex-wrap" data-cy="ai-screening-scores">
        <BLabel
          size="dense"
          :bg-color="scoreBg(aiScreening?.overallScore)"
          data-cy="ai-screening-score-overall"
        >
          {{ t('modules.recruitment.aiScreening.overallScore', { score: aiScreening?.overallScore }) }}
        </BLabel>
        <BLabel
          v-if="aiScreening?.resumeQuality?.score != null"
          size="dense"
          bg-color="base"
          data-cy="ai-screening-score-cv"
        >
          {{ t('modules.recruitment.aiScreening.cvScore', { score: aiScreening.resumeQuality.score }) }}
        </BLabel>
        <BLabel
          v-if="aiScreening?.coverLetterQuality?.score != null"
          size="dense"
          bg-color="base"
          data-cy="ai-screening-score-cl"
        >
          {{ t('modules.recruitment.aiScreening.clScore', { score: aiScreening.coverLetterQuality.score }) }}
        </BLabel>
        <BLabel
          v-if="aiScreening?.jobFit?.score != null"
          size="dense"
          bg-color="base"
          data-cy="ai-screening-score-fit"
        >
          {{ t('modules.recruitment.aiScreening.fitScore', { score: aiScreening.jobFit.score }) }}
        </BLabel>
      </div>

      <p v-if="aiScreening?.overallSummary" class="text-sm leading-6" data-cy="ai-screening-summary">
        {{ aiScreening.overallSummary }}
      </p>

      <div
        v-if="aiScreening?.resumeQuality?.summary"
        class="flex flex-col gap-1"
        data-cy="ai-screening-section-cv"
      >
        <p class="text-xs font-medium">{{ t('modules.recruitment.aiScreening.cvTitle') }}</p>
        <p class="text-sm">{{ aiScreening.resumeQuality.summary }}</p>
      </div>

      <div
        v-if="aiScreening?.coverLetterQuality?.summary"
        class="flex flex-col gap-1"
        data-cy="ai-screening-section-cl"
      >
        <p class="text-xs font-medium">{{ t('modules.recruitment.aiScreening.clTitle') }}</p>
        <p class="text-sm">{{ aiScreening.coverLetterQuality.summary }}</p>
      </div>

      <div
        v-if="aiScreening?.jobFit?.summary"
        class="flex flex-col gap-1"
        data-cy="ai-screening-section-fit"
      >
        <p class="text-xs font-medium">{{ t('modules.recruitment.aiScreening.fitTitle') }}</p>
        <p class="text-sm">{{ aiScreening.jobFit.summary }}</p>
        <ul v-if="aiScreening.jobFit.matchPoints?.length" class="text-sm list-disc pl-4">
          <li v-for="point in aiScreening.jobFit.matchPoints" :key="point">{{ point }}</li>
        </ul>
        <ul v-if="aiScreening.jobFit.gaps?.length" class="text-sm list-disc pl-4 text-subtitle">
          <li v-for="gap in aiScreening.jobFit.gaps" :key="gap">{{ gap }}</li>
        </ul>
      </div>
    </template>

    <template v-else-if="aiScreening?.status === 'failed' || aiScreening?.status === 'skipped'">
      <p class="text-sm text-subtitle" data-cy="ai-screening-error">
        {{ aiScreening?.error || statusLabel }}
      </p>
    </template>
  </div>
</template>
